import { type PropType, defineComponent, defineAsyncComponent } from 'vue';
import { getPrice } from '@i22/rocket';
import { mapActions, mapGetters } from 'vuex';
import { openFlyout } from '~/composables/flyout';
import serializeCartAction from '@/lib/tealium/serializers/cart';
import { handleCartErrors } from '@/helpers/add-to-cart';
import { isAllowedToAddToCart } from '~/lib/magenta-moments';
import { getBundleArticleNumbers, isManualBundle } from '~/lib/build-bundle-from-products';
import type { ListOffer } from '~/lib/goliath/offers';
import type { Item } from '~/lib/types/cartdata';
import type { AddToCart } from '~/store/cart';
import type { Offer } from '~/lib/goliath/offer-by-slugs';
import { tealiumLink } from '~/lib/tealium';
import { trackFBEvent } from '~/lib/tracking-fb';

type ItemFromOffer = {
  articleNumber: string;
  wasPrice: number | undefined;
  slug: string;
  name: string;
  price: number | undefined;
  category: string | undefined;
};
type AddToCartParams = {
  articleNumber: string;
  quantity: number;
};
export type AddToCartItem = Item | ItemFromOffer | AddToCartParams;

export default defineComponent({
  name: 'AddToCart',
  props: {
    offer: {
      type: Object as PropType<ListOffer | Offer>,
      required: true,
      default: () => ({} as ListOffer),
    },
    quantity: {
      type: Number,
      default: 1,
      validator: (value: number) => value >= 1,
    },
  },
  data() {
    return {
      isSubmitting: false,
      stockQuantity: this.offer?.product?.availability?.stock || 0,
      fallbackMessage: 'Hoppla! Da ist etwas schiefgelaufen. Bitte versuche es noch einmal.',
    };
  },
  watch: {
    offer(offer: ListOffer) {
      this.stockQuantity = offer?.product?.availability?.stock || 0;
    },
  },
  computed: {
    ...mapGetters('cart', ['cartItems']),
    tealiumRel(): string {
      return 'content.button.in-den-warenkorb';
    },
    isAllowedToAddToCart(): boolean {
      return isAllowedToAddToCart(this.articleNumber, this.$route.path);
    },
    isDisabled(): boolean {
      if (!this.isAllowedToAddToCart) return true;
      if (this.isSubmitting) return true;
      return !this.inStock;
    },
    articleNumber(): string | null {
      return this.offer?.product?.articleNumber || null;
    },
    cartItem() {
      if (this.articleNumber === null) return undefined;
      // @ts-ignore
      return this.cartItems.find(({ articleNumber }) => articleNumber === this.articleNumber);
    },
    cartQuantity(): number {
      return this.cartItem?.quantity || 0;
    },
    maxQuantity(): number {
      return this.cartItem?.maximumAvailable || this.offer?.maxOrderableAmount || Infinity;
    },
    stockOrMaxQuantity(): number {
      return this.maxQuantity < this.stockQuantity ? this.maxQuantity : this.stockQuantity;
    },
    orderableQuantity(): number {
      // max quantity available - currently in cart - quantity to be put in the cart
      const quantity = this.stockOrMaxQuantity - this.cartQuantity - (this.quantity - 1);

      return quantity >= 0 ? quantity : 0;
    },
    inStock(): boolean {
      return this.orderableQuantity > 0;
    },
    addToCartData(): AddToCart[] | undefined {
      if (!this.articleNumber) return undefined;
      if (isManualBundle(this.articleNumber)) {
        return getBundleArticleNumbers(this.offer as ListOffer).map((articleNumber) => ({
          quantity: 1,
          articleNumber,
        }));
      }
      return [
        {
          articleNumber: this.articleNumber,
          quantity: this.quantity || 1,
        },
      ];
    },
  },
  emits: ['added', 'error'],
  methods: {
    ...mapActions('cart', ['addCartItems']),
    trackAddToCart(items: AddToCartItem[]) {
      try {
        tealiumLink({
          wt_link_id: this.tealiumRel,
          event_name: 'cart_add',
          ...serializeCartAction(undefined, items),
        }).then(() => {});
      } catch (error) {
        // do not break the process
      }
    },
    getItem(articleNumber: string): AddToCartItem | undefined {
      // @ts-ignore
      const item = this.cartItems.find((e: any) => e.articleNumber === articleNumber);
      if (item) return item;
      if (this.offer?.product?.articleNumber === articleNumber) {
        const addToCartItem: ItemFromOffer = {
          articleNumber: this.offer.product.articleNumber,
          price: getPrice(this.offer.prices || [], 'DEFAULT', 'ONETIME')?.amount || undefined,
          wasPrice: getPrice(this.offer.prices || [], 'WAS', 'ONETIME')?.amount || undefined,
          // teaserImage: this.offer.product.teaserImage,
          slug: this.offer.product.slug || '',
          name: this.offer.product.name || '',
          category: this.offer.product.family?.primaryCategory?.name || undefined,
        };
        return addToCartItem;
      }
      return undefined;
    },
    async addOfferToCart() {
      if (this.isDisabled || !this.addToCartData) return false;
      this.isSubmitting = true;
      try {
        // @ts-ignore
        await this.addCartItems(this.addToCartData);
        const articleNumber =
          this.offer?.product?.articleNumber || this.addToCartData[0]?.articleNumber;
        // search Item from cart
        const item = this.getItem(articleNumber) || this.addToCartData[0];
        this.$emit('added', item);

        this.offerAddedToCart([item]);

        // Track after everything is finished for a better ux.
        this.trackAddToCart([item]);
        this.trackFacebook(item);

        return true;
      } catch (error: Error | any) {
        this.$emit('error');
        await handleCartErrors(error, (articleNumber) => {
          const item = this.getItem(articleNumber);
          if (!item) return articleNumber;
          return 'name' in item ? item.name : articleNumber;
        });
      } finally {
        this.isSubmitting = false;
      }
      return false;
    },
    offerAddedToCart(items: AddToCartItem[]) {
      openFlyout({
        ariaLabel: `${items.length} Artikel in den Warenkorb gelegt`,
        component: {
          is: defineAsyncComponent(() => import('~/components/add-to-cart-dialog-content.vue')),
          props: { items },
        },
        success: true,
      });
    },
    trackFacebook(item: AddToCartItem) {
      try {
        trackFBEvent('AddToCart', {
          content_ids: [item.articleNumber],
          content_name: 'name' in item ? item.name : undefined,
          content_type: 'product',
          currency: 'EUR',
          value: 'price' in item && item.price ? item.price / 100 : undefined,
          num_of_items: 'quantity' in item ? item.quantity : 1,
        });
      } catch (_e) {
        // empty
      }
    },
  },
});
